// SuggestSceneFormatsToolUI — branded tool-UI for the agent's `suggestSceneFormats` tool call.
//
// The backend returns a JSON string:
//   { "current": { "slug": "flat-vector", "variant": 1, "name": "Flat vector" } | null,
//     "formats": [ { "slug": "...", "variant": 2, "name": "...", "why": "..." }, ... ] }
//
// Each card shows the catalog thumbnail, name, variant and why. When the result carries `cost`,
// "Redesign in this format" renders the self-contained RedesignSceneState confirm inline (one
// click, no agent round-trip). Without `cost` it falls back to a follow-up user message naming the
// slug + variant; the agent answers with its gated `redesignScene` mutation (confirm card).
//
// Styling: every utility is `tw-`-prefixed and takes effect under `.aidea-chat-root`. No emoji.

import React, { useState } from "react";
import { useStore } from "react-redux";
import { makeAssistantToolUI, useThreadRuntime } from "@assistant-ui/react";

import { SpinnerIcon, ImageGenIcon, AlertIcon } from "../icons";
import { catalogThumb } from "../catalogThumb";
import { RedesignSceneState } from "./BulkMutateToolUI";
import i18n from "../../../../locales/i18n";

// Parse the tool result defensively (string or object; never throw).
export function parseFormatsResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  const current = data.current && typeof data.current === "object" ? data.current : null;
  const formats = Array.isArray(data.formats) ? data.formats.filter((f) => f && f.slug) : [];
  const cost = data.cost && typeof data.cost === "object" ? data.cost : null;
  return { current, formats, cost };
}

function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[520px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

function HeaderChip() {
  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
        <ImageGenIcon size={12} />
        {i18n.t("aideaChat.formats.chip")}
      </span>
    </div>
  );
}

function FormatTile({ format, isCurrent, onSelect, selected }) {
  const thumb = catalogThumb(format.slug, format.variant);
  return (
    <div
      className={[
        "tw-flex tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border",
        selected
          ? "tw-border-[var(--aidea-primary)] tw-ring-1 tw-ring-[var(--aidea-primary)]"
          : "tw-border-[var(--aidea-border)]",
      ].join(" ")}
      style={{ background: "var(--aidea-surface)" }}
    >
      <div className="tw-relative tw-h-[80px] tw-w-full tw-overflow-hidden tw-bg-[var(--aidea-surface-alt,#f4f4f6)]">
        {thumb ? (
          <img
            src={thumb}
            alt={format.name}
            loading="lazy"
            className="tw-h-full tw-w-full tw-object-cover"
          />
        ) : (
          <div
            data-testid="format-thumb-fallback"
            className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]"
          >
            <ImageGenIcon size={22} />
          </div>
        )}
        {isCurrent && (
          <span className="tw-absolute tw-left-1 tw-top-1 tw-rounded-full tw-bg-[var(--aidea-primary)] tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-[var(--aidea-on-primary)]">
            {i18n.t("aideaChat.formats.current")}
          </span>
        )}
      </div>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-px-2 tw-py-1.5">
        <p className="tw-m-0 tw-text-[12px] tw-font-semibold tw-leading-snug tw-text-[var(--aidea-fg)]">
          {format.name || format.slug}
        </p>
        <p className="tw-m-0 tw-text-[11px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
          {i18n.t("aideaChat.formats.variant", { n: format.variant })}
        </p>
        {format.why && (
          <p className="tw-m-0 tw-mt-0.5 tw-text-[11px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
            {format.why}
          </p>
        )}
        <button
          type="button"
          onClick={() => onSelect(format)}
          disabled={selected}
          className="tw-mt-2 tw-cursor-pointer tw-rounded-full tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-px-2 tw-py-1 tw-text-[11.5px] tw-font-medium tw-text-[var(--aidea-fg)] hover:tw-bg-[var(--aidea-muted-bg)] disabled:tw-cursor-default disabled:tw-opacity-60"
        >
          {i18n.t(selected ? "aideaChat.formats.sent" : "aideaChat.formats.redesign")}
        </button>
      </div>
    </div>
  );
}

export function FormatsCard({ current, formats, cost }) {
  const thread = useThreadRuntime();
  const store = useStore();
  const [selectedKey, setSelectedKey] = useState(null);
  const [inline, setInline] = useState(null);
  if (!formats.length) {
    return (
      <Card>
        <HeaderChip />
        <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none" />
          <span className="tw-min-w-0">{i18n.t("aideaChat.formats.empty")}</span>
        </div>
      </Card>
    );
  }
  const onSelect = (f) => {
    setSelectedKey(`${f.slug}-${f.variant}`);
    if (cost) {
      // The card was produced for the scene selected when the author asked.
      const navId = store.getState().undoGroup.present.navItemSelected;
      setInline({
        params: { format: f.slug, variant: f.variant },
        scope: { navId },
      });
      return;
    }
    thread.append({
      role: "user",
      content: [
        {
          type: "text",
          text: i18n.t("aideaChat.formats.redesignMessage", {
            name: f.name || f.slug,
            slug: f.slug,
            variant: f.variant,
          }),
        },
      ],
    });
  };
  return (
    <Card>
      <HeaderChip />
      {current && (
        <p className="tw-m-0 tw-px-3 tw-pt-1.5 tw-text-[12px] tw-text-[var(--aidea-muted-fg)]">
          {i18n.t("aideaChat.formats.currentLine")}{" "}
          <span className="tw-font-medium tw-text-[var(--aidea-fg)]">
            {current.name || current.slug}
          </span>
        </p>
      )}
      <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-px-3 tw-pb-3 tw-pt-2">
        {formats.slice(0, 3).map((f) => (
          <FormatTile
            key={`${f.slug}-${f.variant}`}
            format={f}
            isCurrent={!!current && current.slug === f.slug && current.variant === f.variant}
            onSelect={onSelect}
            selected={selectedKey === `${f.slug}-${f.variant}`}
          />
        ))}
      </div>
      {inline && (
        <RedesignSceneState
          key={`${inline.scope.navId}-${inline.params.format}-${inline.params.variant}`}
          op="redesignScene"
          params={inline.params}
          scope={inline.scope}
          cost={cost}
        />
      )}
    </Card>
  );
}

export const SuggestSceneFormatsToolUI = makeAssistantToolUI({
  toolName: "suggestSceneFormats",
  render: ({ result, status }) => {
    if (status && status.type === "running") {
      return (
        <Card>
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
            <SpinnerIcon
              size={15}
              className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]"
            />
            <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
              {i18n.t("aideaChat.formats.loading")}
            </span>
          </div>
        </Card>
      );
    }
    const parsed = parseFormatsResult(result);
    if (!parsed) {
      return (
        <Card>
          <HeaderChip />
          <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
            <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none" />
            <span className="tw-min-w-0">{i18n.t("aideaChat.formats.failed")}</span>
          </div>
        </Card>
      );
    }
    return <FormatsCard current={parsed.current} formats={parsed.formats} cost={parsed.cost} />;
  },
});

export default SuggestSceneFormatsToolUI;
